<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';

const emits = defineEmits(['review-sent']);
const isShowModal = defineModel<boolean>({ default: false });
const { product } = defineProps<{
  product: {
    product_id: number;
    product_name: string;
    offer_images: {
      conversions?: { thumb_url_360: string };
      conversions_webp?: { thumb_url_360: string };
      is_main?: boolean;
      url: string;
    }[];
  };
}>();
const feedbackStore = useFeedbackStore();

const initialReviewState = {
  model: 'product',
  model_id: product.product_id,
  advantages: '',
  disadvantages: '',
  comment: '',
  is_anonymous: false,
  rating: 0,
  images: [] as File[],
};

const review = ref(structuredClone(initialReviewState));
const errors = ref({ rating: '', comment: '' });
const isShowRatingError = computed(() => !!errors.value.rating.length);
const isLoading = ref(false);
const previews = ref<string[]>([]);

const productCatalogImage = computed(() => {
  if (product) {
    return product.offer_images.find((image) => image.is_main) ?? product.offer_images[0];
  }

  return null;
});

watch(
  () => review.value,
  () => {
    if (review.value.rating) errors.value.rating = '';
    if (review.value.comment) errors.value.comment = '';
  },
  { deep: true },
);

const hasErrors = computed(() => !!errors.value.rating || !!errors.value.comment);

async function sendReview() {
  if (!review.value.rating) errors.value.rating = 'Укажите рейтинг';
  if (!review.value.comment) errors.value.comment = 'Поле обязательно для заполнения';

  if (hasErrors.value) return;

  isLoading.value = true;

  const isSuccess = await feedbackStore.sendReview(review.value);

  if (isSuccess) {
    const toast = useToast();
    toast.success({
      component: ToastComponent,
      props: { text: 'Отзыв успешно отправлен! Он появится на странице товара после модерации.' },
    });

    review.value = structuredClone(initialReviewState);
    previews.value = [];
    isShowModal.value = false;
    emits('review-sent');
  }

  isLoading.value = false;
}
</script>

<template>
  <BaseModal
    v-model="isShowModal"
    :is-loading="isLoading"
    confirm-btn-text="Отправить"
    title="Оставить свой отзыв"
    @close="isShowModal = false"
    @confirm="sendReview"
  >
    <section class="add-review-modal">
      <div class="flex gap-6 items-center">
        <picture
          v-if="productCatalogImage"
          class="size-20 object-contain rounded-xl"
        >
          <source
            v-if="productCatalogImage.conversions_webp?.thumb_url_360"
            :srcset="productCatalogImage.conversions_webp.thumb_url_360"
            type="image/webp"
          />
          <source
            v-if="productCatalogImage.conversions?.thumb_url_360"
            :srcset="productCatalogImage.conversions.thumb_url_360"
            type="image/jpeg"
          />
          <img
            :alt="product.product_name"
            :src="productCatalogImage.url"
            class="w-full h-full object-contain"
          />
        </picture>

        <div v-else>
          <SvgoNoPhotoSmall
            :font-controlled="false"
            class="size-14"
            filled
          />
        </div>

        <div>{{ product.product_name }}</div>
      </div>

      <div class="add-review-modal__section">
        <p class="add-review-modal__section-header">
          Оцените товар<span v-if="review.rating">: {{ review.rating.toFixed(1) }}</span>
        </p>

        <RatingStars
          :rating="review.rating"
          editable
          size="8"
          @change="review.rating = $event"
        />

        <FoldableBlock v-model="isShowRatingError">
          <p class="add-review-modal__section-error">
            {{ errors.rating }}
          </p>
        </FoldableBlock>
      </div>

      <div class="add-review-modal__section">
        <p class="add-review-modal__section-header">Преимущества</p>

        <TextareaField
          v-model="review.advantages"
          class="w-full"
          name="product review advantages"
          placeholder="Опишите преимущества"
        />
      </div>

      <div class="add-review-modal__section">
        <p class="add-review-modal__section-header">Недостатки</p>

        <TextareaField
          v-model="review.disadvantages"
          class="w-full"
          name="product review disadvantages"
          placeholder="Опишите слабые стороны товара"
        />
      </div>

      <div class="add-review-modal__section">
        <p class="add-review-modal__section-header">Комментарий</p>

        <TextareaField
          v-model="review.comment"
          :error="errors.comment"
          class="w-full"
          name="product review comment"
          placeholder="В свободной форме"
        />
      </div>

      <ProductPageFeedbackImagesUploadBlock
        v-model:images="review.images"
        v-model:previews="previews"
      />

      <Checkbox v-model="review.is_anonymous"> Анонимный отзыв </Checkbox>
    </section>
  </BaseModal>
</template>

<style lang="scss" scoped>
.add-review-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    width: 100%;

    &-header {
      font-weight: $bold;
      font-size: $text-sm;
    }

    &-error {
      color: $hell-red;
      font-size: $text-sm;
    }
  }

  &__images {
    display: flex;
    gap: 8px;
    overflow: auto;
    width: 100%;
  }

  &__image-box {
    position: relative;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    object-fit: cover;
    overflow: hidden;
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background-color: $hell-black-10;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.7;
    }
  }

  &__image-remove {
    position: absolute;
    top: 3px;
    right: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 14px;
    height: 14px;
    padding: 2px 1px 0;
    border-radius: 100%;
    background-color: $white;
    cursor: pointer;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>
